import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/zoom";

import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Keyboard, Navigation, Pagination, Zoom } from "swiper/modules";
import { useTranslation } from "react-i18next";

import AlbumService, { albumImageSrc } from "../services/album.service";
import { matchesQuery } from "../utils/search";
import Spinner from "./Spinner";
import useSeo, { absoluteUrl, toPlainText } from "../seo/useSeo";

// Public gallery. Two modes:
//  - /gallery        → grid of all published albums
//  - /gallery/:id    → a single album viewed as a Swiper slideshow
const Gallery = () => {
  const { t } = useTranslation();
  const { id } = useParams();

  const [albums, setAlbums] = useState([]);
  const [album, setAlbum] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // Fullscreen lightbox: index of the opened slide, or null when closed.
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const lightboxSwiper = useRef(null);
  // Album-grid search by name (client-side; all albums are already loaded).
  const [query, setQuery] = useState("");

  const resolveMsg = (err) =>
    (err.response && err.response.data && err.response.data.message) || err.message;

  // Lock page scroll + close on Escape while the lightbox is open.
  useEffect(() => {
    if (lightboxIndex === null) return undefined;
    const body = document.body;
    const scrollY = window.scrollY;
    const prev = { overflow: body.style.overflow, position: body.style.position, top: body.style.top, width: body.style.width };
    body.style.overflow = "hidden";
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.width = "100%";
    const onKey = (e) => {
      if (e.key === "Escape") setLightboxIndex(null);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      Object.assign(body.style, prev);
      window.scrollTo(0, scrollY);
      window.removeEventListener("keydown", onKey);
    };
  }, [lightboxIndex]);

  const zoomIn = () => lightboxSwiper.current && lightboxSwiper.current.zoom.in();
  const zoomOut = () => lightboxSwiper.current && lightboxSwiper.current.zoom.out();

  useEffect(() => {
    setLoading(true);
    setError("");
    setAlbum(null);
    setLightboxIndex(null);
    if (id) {
      AlbumService.get(id)
        .then((res) => setAlbum(res.data))
        .catch((err) => setError(resolveMsg(err)))
        .finally(() => setLoading(false));
    } else {
      AlbumService.getAll()
        .then((res) => setAlbums(res.data || []))
        .catch((err) => setError(resolveMsg(err)))
        .finally(() => setLoading(false));
    }
  }, [id]);

  // `album` is null on the first render after navigating /gallery -> /gallery/:id
  // (the fetch effect hasn't run yet), so treat that as loading too.
  const albumImgs = (album && album.images) || [];
  const albumImg = albumImageSrc;
  useSeo(
    id && album
      ? {
          title: album.title,
          description: toPlainText(album.description) || album.title,
          image: albumImgs[0] ? albumImg(albumImgs[0].url) : undefined,
          canonical: `/gallery/${album.id}`,
          jsonLd: {
            "@context": "https://schema.org",
            "@type": "ImageGallery",
            name: album.title,
            description: toPlainText(album.description, 500),
            url: absoluteUrl(`/gallery/${album.id}`),
            image: albumImgs.slice(0, 20).map((im) => absoluteUrl(albumImg(im.url))),
          },
        }
      : id && error
      ? { title: t("album.galleryTitle", "Gallery"), noindex: true }
      : null
  );

  if (loading || (id && !album)) {
    return <Spinner />;
  }

  if (error) {
    return (
      <div className="container mt-5">
        <div className="alert alert-danger">{error}</div>
        <Link to="/gallery" className="btn btn-outline-secondary">
          ← {t("album.backToGallery", "Back to gallery")}
        </Link>
      </div>
    );
  }

  // ---- Single album slideshow ----
  if (id) {
    const images = (album && album.images) || [];
    return (
      <div className="container album-page mt-3 mt-md-4 mb-5">
        <div className="d-flex justify-content-between align-items-start mb-3 flex-wrap gap-2">
          <div className="album-page__head">
            <h2 className="fw-bold mb-1 album-page__title">{album.title}</h2>
            {album.description && <p className="text-muted mb-0">{album.description}</p>}
          </div>
          <Link to="/gallery" className="btn btn-outline-secondary btn-sm">
            ← {t("album.backToGallery", "Back to gallery")}
          </Link>
        </div>

        {images.length === 0 ? (
          <p className="text-muted">{t("album.noImages", "This album has no images.")}</p>
        ) : (
          <>
            <Swiper
              modules={[Navigation, Pagination, Autoplay, Zoom, Keyboard]}
              spaceBetween={16}
              slidesPerView={1}
              navigation
              keyboard={{ enabled: true }}
              pagination={{ clickable: true }}
              autoplay={{ delay: 4000, disableOnInteraction: true, pauseOnMouseEnter: true }}
              zoom={{ maxRatio: 3, toggle: true }}
              loop={images.length > 1}
              className="album-swiper rounded"
            >
              {images.map((img, idx) => (
                <SwiperSlide key={img.id}>
                  <div className="album-slide">
                    {/* Double-click / pinch to zoom inline */}
                    <div className="swiper-zoom-container album-slide__zoom">
                      <img src={albumImageSrc(img.url)} alt={img.caption || album.title} />
                    </div>
                    <button
                      type="button"
                      className="album-slide__expand"
                      onClick={() => setLightboxIndex(idx)}
                      title={t("album.fullscreen", "View fullscreen")}
                      aria-label={t("album.fullscreen", "View fullscreen")}
                    >
                      ⤢
                    </button>
                    <div className="album-slide__caption">
                      <span className="album-slide__counter">
                        {idx + 1} / {images.length}
                      </span>
                      <div className="album-slide__title">{img.caption || album.title}</div>
                      {!img.caption && album.description && (
                        <div className="album-slide__desc">{album.description}</div>
                      )}
                    </div>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
            <p className="text-center text-muted small mt-2">
              {t("album.zoomHint", "Double-click (or pinch) an image to zoom. Click ⤢ for fullscreen.")}
            </p>

            {/* Thumbnails */}
            <div className="album-thumbs d-flex flex-wrap gap-2 justify-content-center mt-2">
              {images.map((img, idx) => (
                <img
                  key={img.id}
                  src={albumImageSrc(img.url)}
                  alt={img.caption || ""}
                  title={img.caption || ""}
                  onClick={() => setLightboxIndex(idx)}
                />
              ))}
            </div>

            {lightboxIndex !== null && (
              <div className="album-lightbox" role="dialog" aria-modal="true">
                <div className="album-lightbox__toolbar">
                  <button type="button" onClick={zoomIn} title={t("album.zoomIn", "Zoom in")}>＋</button>
                  <button type="button" onClick={zoomOut} title={t("album.zoomOut", "Zoom out")}>－</button>
                  <button
                    type="button"
                    onClick={() => setLightboxIndex(null)}
                    title={t("album.close", "Close")}
                  >
                    ✕
                  </button>
                </div>
                <Swiper
                  modules={[Navigation, Pagination, Zoom, Keyboard]}
                  initialSlide={lightboxIndex}
                  onSwiper={(sw) => (lightboxSwiper.current = sw)}
                  navigation
                  keyboard={{ enabled: true }}
                  pagination={{ type: "fraction" }}
                  zoom={{ maxRatio: 5, toggle: true }}
                  className="album-lightbox__swiper"
                >
                  {images.map((img) => (
                    <SwiperSlide key={img.id}>
                      <div className="swiper-zoom-container">
                        <img src={albumImageSrc(img.url)} alt={img.caption || album.title} />
                      </div>
                      {(img.caption || album.title) && (
                        <div className="album-lightbox__caption">
                          <div className="album-slide__title">{img.caption || album.title}</div>
                          {!img.caption && album.description && (
                            <div className="album-slide__desc">{album.description}</div>
                          )}
                        </div>
                      )}
                    </SwiperSlide>
                  ))}
                </Swiper>
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  // ---- Album grid ----
  const cover = (a) => {
    const img = (a.images || [])[0];
    return img ? albumImageSrc(img.url) : null;
  };

  const visibleAlbums = albums.filter((a) => matchesQuery(query, a.title));

  return (
    <div className="container mt-4 mb-5">
      <div className="text-center mb-4">
        <h2 className="fw-bold" id="gallery">{t("album.galleryTitle", "Gallery")}</h2>
        <p className="text-muted">{t("album.gallerySubtitle", "Browse our photo albums")}</p>
      </div>

      {albums.length > 0 && (
        <div className="row justify-content-center mb-4">
          <div className="col-12 col-md-8 col-lg-6">
            <div className="input-group">
              <input
                type="search"
                className="form-control"
                placeholder={t("album.searchPlaceholder", "Search albums by name...")}
                aria-label={t("album.searchPlaceholder", "Search albums by name...")}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              {query && (
                <button type="button" className="btn btn-outline-secondary" onClick={() => setQuery("")}>
                  {t("album.clearSearch", "Clear")}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {albums.length === 0 ? (
        <p className="text-center text-muted">{t("album.empty", "No albums yet.")}</p>
      ) : visibleAlbums.length === 0 ? (
        <p className="text-center text-muted">
          {t("album.noResults", "No albums match your search.")}
        </p>
      ) : (
        <div className="row g-4">
          {visibleAlbums.map((a) => (
            <div className="col-12 col-sm-6 col-lg-4" key={a.id}>
              <Link to={`/gallery/${a.id}`} className="text-decoration-none text-reset">
                <div className="card h-100 product-card">
                  {cover(a) ? (
                    <img
                      src={cover(a)}
                      className="card-img-top"
                      alt={a.title}
                      style={{ height: 220, objectFit: "cover" }}
                    />
                  ) : (
                    <div
                      className="card-img-top d-flex align-items-center justify-content-center bg-light text-muted"
                      style={{ height: 220 }}
                    >
                      {t("common.noImage")}
                    </div>
                  )}
                  <div className="card-body">
                    <h5 className="card-title mb-1">{a.title}</h5>
                    <div className="small text-muted">
                      {(a.images || []).length} {t("album.photos", "Photos")}
                    </div>
                    {a.description && (
                      <p className="card-text small text-muted mt-2 mb-0 text-truncate">
                        {a.description}
                      </p>
                    )}
                  </div>
                </div>
              </Link>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Gallery;
